/**
 * What each role can do on each screen. The Worker still enforces it. This is the line the page
 * shows so a hidden section has a reason.
 */
import type { ReactNode } from "react";
import { createContext, createElement, useContext } from "react";
import type { StaffRole } from "@lattice/core";
import type { Screen } from "./router.ts";

const RoleContext = createContext<StaffRole | null>(null);

export function RoleProvider({
  role,
  children,
}: {
  readonly role: StaffRole | null;
  readonly children: ReactNode;
}) {
  return createElement(RoleContext.Provider, { value: role }, children);
}

export function useStaffRole(): StaffRole | null {
  return useContext(RoleContext);
}

export const FIELD_INFO = {
  scope:
    "What the clinic can see. Clinic-created is records the clinic made. Everything is the full record.",
  licence: "Practitioner licence. The clinic cannot submit a formula until this is verified.",
} as const;

const LEAD: Readonly<Record<Screen["kind"], string>> = {
  funnel: "Where each customer is in onboarding.",
  search: "One collection at a time. Chips switch accounts, records and clinics.",
  user: "The account, its clinics and what staff have done.",
  organization: "Members, linked patients and link changes.",
  patient: "Labs, files and the clinics that can see this record.",
  jobs: "Extraction runs and formula drafts.",
  reviews: "Cases waiting for a clinician, oldest first.",
  review: "Intake, labs and the draft to sign or decline.",
  orders: "Pharmacy orders, exceptions first.",
  pharmacyOrder: "Status, exceptions and the next action.",
  reactions: "Open reports of a reaction to a formula.",
  catalog: "Ingredients the engine may use, and their limits.",
  rules: "Candidate rule sets. Live drafting changes only on promote.",
  evaluation: "De-identified cases. A run compares a version to approved formulas.",
  audit: "Who did what, and why. Not the clinical record.",
  missing: "This address is not a screen.",
};

export function leadFor(kind: Screen["kind"]): string {
  return LEAD[kind];
}

const PERMIT = {
  "clinician:funnel": "You can open accounts. You cannot change billing or orders.",
  "support:funnel": "You can open accounts and lock customers. You cannot approve a formula.",
  "operations:funnel": "You can open accounts. You cannot approve a formula.",
  "superuser:funnel": "You can open every record. Approval is on Reviews.",
  "engine:funnel": "You can edit catalog, rules and evaluation. Names are not shown.",
  "clinician:search": "You can open a match. You cannot change billing or orders.",
  "support:search": "You can open a match. Changes happen on the record.",
  "operations:search": "You can open a match. Changes happen on the record.",
  "superuser:search": "You can open a match. Changes happen on the record.",
  "engine:search": "Search is hidden. Engine staff do not see names.",
  "clinician:user": "You can open the record. You cannot lock the account or change billing.",
  "support:user": "You can lock a customer account. You cannot lock staff or approve a formula.",
  "operations:user": "You can verify a licence. You cannot lock an account.",
  "superuser:user": "You can lock, verify and change billing. Approval is on Reviews.",
  "engine:user": "This account is hidden. Engine staff do not see names.",
  "clinician:organization": "You can read this clinic. You cannot move or remove a link.",
  "support:organization": "You can move or remove a link. You cannot approve a formula.",
  "operations:organization": "You can read this clinic. You cannot move or remove a link.",
  "superuser:organization": "You can move or remove a link. You cannot approve a formula here.",
  "engine:organization": "This clinic is hidden. Engine staff do not see names.",
  "clinician:patient": "You can read this record. Opening it is logged. You cannot edit the labs.",
  "support:patient": "You can read this record. Opening it is logged. You cannot edit the labs.",
  "operations:patient": "You can read this record. Opening it is logged. You cannot edit the labs.",
  "superuser:patient": "You can read this record. Opening it is logged. You cannot edit the labs.",
  "engine:patient": "This record is hidden. Engine staff do not see names.",
  "clinician:reviews": "You can approve, edit or decline. You cannot change billing.",
  "superuser:reviews": "You can approve, edit or decline. You cannot change billing.",
  "clinician:review": "You can approve, edit or decline. You cannot change billing.",
  "superuser:review": "You can approve, edit or decline. You cannot change billing.",
  "engine:reviews": "Reviews are hidden. They name patients.",
  "engine:review": "Reviews are hidden. They name patients.",
  "clinician:jobs": "You can see jobs. You cannot retry a file.",
  "support:jobs": "You can retry a failed file. You cannot change the formula.",
  "operations:jobs": "You can retry a failed file. You cannot change the formula.",
  "superuser:jobs": "You can retry a failed file. You cannot change the formula.",
  "engine:jobs": "Jobs are hidden. They name patients.",
  "support:orders": "You can resubmit, cancel and record status. You cannot change the formula.",
  "operations:orders": "You can resubmit, cancel and record status. You cannot change the formula.",
  "superuser:orders": "You can resubmit, cancel and record status. You cannot change the formula.",
  "clinician:orders": "Orders are hidden. Clinicians do not change fulfillment.",
  "engine:orders": "Orders are hidden. Engine staff do not see names.",
  "support:pharmacyOrder":
    "You can resubmit, cancel and record status. You cannot change the formula.",
  "operations:pharmacyOrder":
    "You can resubmit, cancel and record status. You cannot change the formula.",
  "superuser:pharmacyOrder":
    "You can resubmit, cancel and record status. You cannot change the formula.",
  "clinician:pharmacyOrder": "Orders are hidden. Clinicians do not change fulfillment.",
  "engine:pharmacyOrder": "Orders are hidden. Engine staff do not see names.",
  "clinician:reactions": "You can mark a report resolved. You cannot change the formula.",
  "support:reactions": "You can mark a report resolved. You cannot change the formula.",
  "operations:reactions": "You can mark a report resolved. You cannot change the formula.",
  "superuser:reactions": "You can mark a report resolved. You cannot change the formula.",
  "engine:reactions": "Reactions are hidden. They name patients.",
  "clinician:audit": "You can read who acted. Lab values stay on the patient record.",
  "support:audit": "You can read who acted. Lab values stay on the patient record.",
  "operations:audit": "You can read who acted. Lab values stay on the patient record.",
  "superuser:audit": "You can read who acted. Lab values stay on the patient record.",
  "engine:audit": "Audit is hidden. The log names people.",
  "engine:catalog": "You can add and edit ingredients. You cannot see a patient name.",
  "superuser:catalog": "You can add and edit ingredients. You cannot see a patient name here.",
  "engine:rules": "You can save a candidate and promote it. Live drafting waits for promotion.",
  "superuser:rules": "You can save a candidate and promote it. Live drafting waits for promotion.",
  "engine:evaluation": "You can run and rate cases. Names are not on this screen.",
  "superuser:evaluation": "You can run and rate cases. Names are not on this screen.",
  "clinician:catalog": "The catalog is for the engine role.",
  "support:catalog": "The catalog is for the engine role.",
  "operations:catalog": "The catalog is for the engine role.",
  "clinician:rules": "Rule versions are for the engine role.",
  "support:rules": "Rule versions are for the engine role.",
  "operations:rules": "Rule versions are for the engine role.",
  "clinician:evaluation": "Evaluation is for the engine role.",
  "support:evaluation": "Evaluation is for the engine role.",
  "operations:evaluation": "Evaluation is for the engine role.",
  "clinician:missing": "Check the address, or search from the rail.",
  "support:missing": "Check the address, or search from the rail.",
  "operations:missing": "Check the address, or search from the rail.",
  "superuser:missing": "Check the address, or search from the rail.",
  "engine:missing": "Check the address. Search is not available for this role.",
} satisfies Record<string, string>;

export function permitFor(role: StaffRole, kind: Screen["kind"]): string {
  const key = `${role}:${kind}`;

  for (const [entry, line] of Object.entries(PERMIT)) {
    if (entry === key) return line;
  }

  return "Your role limits what you can change on this screen.";
}
